<template>
  <v-dialog
    v-model="isVisible"
    max-width="450"
    persistent
    class="auth-required-modal"
  >
    <v-card class="auth-modal-card">
      <v-card-title class="auth-modal-header">
        <v-icon class="mr-2" color="primary" size="28">mdi-account-lock</v-icon>
        Sign In Required
      </v-card-title>

      <v-card-text class="auth-modal-content">
        <p class="text-body-1 mb-4">
          {{ message }}
        </p>
        <div class="feature-list">
          <div class="feature-item">
            <v-icon color="success" size="20">mdi-check-circle</v-icon>
            <span>IS 456/800 compliant design</span>
          </div>
          <div class="feature-item">
            <v-icon color="success" size="20">mdi-check-circle</v-icon>
            <span>Professional PDF reports</span>
          </div>
        </div>
        <p class="text-body-2 text-secondary mt-4">
          <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
          Your inputs will be restored after sign in.
        </p>
      </v-card-text>

      <v-card-actions class="auth-modal-actions">
        <se-button
          variant="text"
          @click="handleCancel"
        >
          Cancel
        </se-button>
        <v-spacer />
        <se-button
          variant="primary"
          @click="handleSignIn"
        >
          Sign In Free
        </se-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import SeButton from '@/components/SEButton.vue'

interface AuthRequiredEventDetail {
  message: string
  returnUrl: string
  calculatorType?: string
  preservedInputs?: boolean
}

const router = useRouter()
const isVisible = ref(false)
const message = ref('Design calculators require a free account')
const returnUrl = ref('/')

const handleAuthRequired = (event: Event) => {
  const customEvent = event as CustomEvent<AuthRequiredEventDetail>
  const detail = customEvent.detail

  message.value = detail.message || 'Design calculators require a free account'
  returnUrl.value = detail.returnUrl || window.location.pathname

  // Store return URL in sessionStorage for post-login redirect
  sessionStorage.setItem('authReturnUrl', returnUrl.value)

  isVisible.value = true
}

const handleSignIn = () => {
  isVisible.value = false
  router.push({
    path: '/login',
    query: { redirect: returnUrl.value }
  })
}

const handleCancel = () => {
  isVisible.value = false
}

onMounted(() => {
  window.addEventListener('auth:required', handleAuthRequired)
})

onUnmounted(() => {
  window.removeEventListener('auth:required', handleAuthRequired)
})
</script>

<style scoped>
.auth-modal-card {
  border-radius: var(--radius-xl);
  background: var(--bg-surface);
}

.auth-modal-header {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: 600;
  padding: 24px 24px 16px;
  color: var(--text-primary);
}

.auth-modal-content {
  padding: 0 24px 16px;
}

.feature-list {
  background: var(--bg-canvas);
  border-radius: var(--radius-lg);
  padding: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  color: var(--text-body);
  font-size: var(--font-size-sm);
}

.feature-item:not(:last-child) {
  border-bottom: 1px solid var(--border-subtle);
}

.auth-modal-actions {
  padding: 16px 24px 24px;
}

.text-secondary {
  color: var(--text-muted);
}
</style>
